import type { PerformanceSource, TradeEntry } from '@/utils/performanceSource';
import { buildWeeklyEquity, buildMonthlyEquity, type EquityPoint } from '@/utils/weeklyEquity';

/**
 * Pure derivation layer for the Performance page.
 *
 * Nothing here touches the network or imports a fixed data file. Instead,
 * `derivePerformanceData(source)` receives the raw trade log that was loaded at
 * runtime from `public/data/performance.json` and returns every number, table
 * and curve the page needs. Refreshing the account therefore requires editing
 * that one JSON file — all of this recomputes automatically.
 */

export interface MonthlyRow {
  id: string;
  month: string;
  axisLabel: string;
  year: number;
  profit: number;
  pct: number;
  balance: number;
  trades: number;
  winRate: number;
}

export interface ReturnRow {
  month: string;
  short: string;
  axisLabel: string;
  year: number;
  pct: number;
}

export interface HeadlineMetric {
  id: string;
  label: string;
  value: string;
  caption: string;
  tone: 'accent' | 'primary' | 'secondary';
}

export interface DetailMetric {
  id: string;
  label: string;
  value: string;
}

export interface AccountSnapshot {
  accountNumber: string;
  broker: string;
  platform: string;
  accountType: string;
  currency: string;
  startingDeposit: number;
  withdrawal: number;
  balance: number;
  equity: number;
  peakBalance: number;
  currentDrawdown: number;
  netProfit: number;
  totalReturn: number;
  totalTrades: number;
  openPositions: number;
  updatedAt: string;
  verification: string;
}

export interface DerivedPerformance {
  trades: TradeEntry[];
  headlineMetrics: HeadlineMetric[];
  detailMetrics: DetailMetric[];
  accountSnapshot: AccountSnapshot;
  monthlyBreakdown: MonthlyRow[];
  monthlyReturns: ReturnRow[];
  bestMonth: MonthlyRow;
  weeklyEquity: EquityPoint[];
  monthlyEquity: EquityPoint[];
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const round2 = (value: number) => Math.round(value * 100) / 100;

export function derivePerformanceData(source: PerformanceSource): DerivedPerformance {
  const { startingBalance, accountMeta } = source;

  /* Chronological order; 'YYYY.MM.DD' already sorts correctly as a string. */
  const chrono = [...source.trades].sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));

  const totalTrades = chrono.length;
  const wins = chrono.filter(([, pnl]) => pnl >= 0).length;
  const losses = totalTrades - wins;

  const grossProfit = chrono.filter(([, pnl]) => pnl > 0).reduce((sum, [, pnl]) => sum + pnl, 0);
  const grossLoss = chrono
    .filter(([, pnl]) => pnl < 0)
    .reduce((sum, [, pnl]) => sum + Math.abs(pnl), 0);

  const netProfit = round2(grossProfit - grossLoss);
  /** Capital actually at work: deposit minus the one withdrawal already taken. */
  const capitalBase = round2(startingBalance - accountMeta.withdrawal);
  const balance = round2(capitalBase + netProfit);
  const totalReturn = capitalBase > 0 ? round2((netProfit / capitalBase) * 100) : 0;
  const winRate = totalTrades > 0 ? (wins / totalTrades) * 100 : 0;
  const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : 0;
  const averageWin = wins > 0 ? grossProfit / wins : 0;
  const averageLoss = losses > 0 ? grossLoss / losses : 0;

  const lastDate = chrono[chrono.length - 1]?.[0] ?? '';

  /* ---------------------------------------------------------------- */
  /* Month-by-month results (the backbone of the tables, bars & equity) */
  /* ---------------------------------------------------------------- */

  const monthTotals = new Map<string, number>();
  chrono.forEach(([date, pnl]) => {
    const [year, month] = date.split('.').map(Number);
    const key = `${year}-${String(month).padStart(2, '0')}`;
    monthTotals.set(key, (monthTotals.get(key) ?? 0) + pnl);
  });

  /** Per-month trade counts + winners, used for the monthly win-rate chart. */
  const monthCounts = new Map<string, number>();
  const monthWins = new Map<string, number>();
  chrono.forEach(([date, pnl]) => {
    const [year, month] = date.split('.').map(Number);
    const key = `${year}-${String(month).padStart(2, '0')}`;
    monthCounts.set(key, (monthCounts.get(key) ?? 0) + 1);
    if (pnl >= 0) {
      monthWins.set(key, (monthWins.get(key) ?? 0) + 1);
    }
  });

  const monthKeys = [...monthTotals.keys()].sort();

  let runningBalance = capitalBase;
  const monthlyBreakdown: MonthlyRow[] = monthKeys.map((key) => {
    const [year, month] = key.split('-').map(Number);
    const profit = round2(monthTotals.get(key) ?? 0);
    const pct = runningBalance > 0 ? round2((profit / runningBalance) * 100) : 0;
    runningBalance = round2(runningBalance + profit);
    const trades = monthCounts.get(key) ?? 0;
    const wins = monthWins.get(key) ?? 0;
    return {
      id: key,
      month: `${MONTHS[month - 1]} ${year}`,
      axisLabel: `${MONTHS[month - 1]} '${String(year).slice(2)}`,
      year,
      profit,
      pct,
      balance: runningBalance,
      trades,
      winRate: trades > 0 ? round2((wins / trades) * 100) : 0,
    };
  });

  const monthlyReturns: ReturnRow[] = monthlyBreakdown.map((row) => {
    const [year, month] = row.id.split('-').map(Number);
    return {
      month: `${MONTHS[month - 1]} ${String(year).slice(2)}`,
      short: MONTHS[month - 1],
      axisLabel: row.axisLabel,
      year,
      pct: row.pct,
    };
  });

  const firstMonth = monthlyBreakdown[0];
  const bestMonth = monthlyBreakdown.reduce(
    (best, current) => (current.pct > best.pct ? current : best),
    firstMonth ?? { id: '', month: '', axisLabel: '', year: 0, profit: 0, pct: 0, balance: 0, trades: 0, winRate: 0 },
  );

  /* ---------------------------------------------------------------- */
  /* Headline + detail metrics                                        */
  /* ---------------------------------------------------------------- */

  const headlineMetrics: HeadlineMetric[] = [
    {
      id: 'total-return',
      label: 'Total Return',
      value: `${totalReturn >= 0 ? '+' : ''}${totalReturn.toFixed(2)}%`,
      caption: `real account, since ${firstMonth?.month ?? ''}`,
      tone: 'accent',
    },
    {
      id: 'win-rate',
      label: 'Win Rate',
      value: `${winRate.toFixed(1)}%`,
      caption: `${wins} of ${totalTrades} closed trades`,
      tone: 'primary',
    },
    {
      id: 'profit-factor',
      label: 'Profit Factor',
      value: profitFactor.toFixed(2),
      caption: 'gross profit ÷ gross loss',
      tone: 'primary',
    },
    {
      id: 'max-dd',
      label: 'Max Drawdown',
      value: accountMeta.maxDrawdown,
      caption: 'peak-to-valley, incl. floating equity',
      tone: 'secondary',
    },
  ];

  const detailMetrics: DetailMetric[] = [
    { id: 'sharpe', label: 'Sharpe Ratio', value: accountMeta.sharpeRatio },
    { id: 'recovery', label: 'Recovery Factor', value: accountMeta.recoveryFactor },
    { id: 'avg-win', label: 'Average Win', value: `+${averageWin.toFixed(2)} USC` },
    { id: 'avg-loss', label: 'Average Loss', value: `-${averageLoss.toFixed(2)} USC` },
    { id: 'trades', label: 'Total Closed Trades', value: `${totalTrades}` },
  ];

  /* ---------------------------------------------------------------- */
  /* Account snapshot (identity from meta, money figures derived)     */
  /* ---------------------------------------------------------------- */

  /**
   * Highest running balance the account ever reached (its all-time peak) and
   * how far the current balance sits below that peak — the "underwater" depth
   * right now, as opposed to the historical max drawdown from the broker meta.
   */
  let runningPeak = capitalBase;
  let runningAcc = capitalBase;
  chrono.forEach(([, pnl]) => {
    runningAcc = round2(runningAcc + pnl);
    if (runningAcc > runningPeak) runningPeak = runningAcc;
  });
  const peakBalance = round2(runningPeak);
  const currentDrawdown = peakBalance > 0 ? round2((balance / peakBalance - 1) * 100) : 0;

  const accountSnapshot: AccountSnapshot = {
    accountNumber: accountMeta.accountNumber,
    broker: accountMeta.broker,
    platform: accountMeta.platform,
    accountType: accountMeta.accountType,
    currency: accountMeta.currency,
    startingDeposit: accountMeta.startingDeposit,
    withdrawal: accountMeta.withdrawal,
    balance,
    equity: balance,
    peakBalance,
    currentDrawdown,
    netProfit,
    totalReturn,
    totalTrades,
    openPositions: accountMeta.openPositions,
    updatedAt: lastDate.replace(/\./g, '-'),
    verification: accountMeta.verification,
  };

  return {
    /* Always hand back the chronological series, never the raw file order —
       some broker exports list trades newest-first, which would otherwise
       silently scramble the "recent trades" list and its running total. */
    trades: chrono,
    headlineMetrics,
    detailMetrics,
    accountSnapshot,
    monthlyBreakdown,
    monthlyReturns,
    bestMonth,
    weeklyEquity: buildWeeklyEquity(source),
    monthlyEquity: buildMonthlyEquity(source),
  };
}